import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getSubscriptionTiers, getUsage, createCheckout } from '../lib/api';
import { Check, X, Loader2, Zap, Calculator, ArrowRight, DollarSign, Shield } from 'lucide-react';
import { trackBeginCheckout, trackUpgradeClicked } from '../lib/analytics';

/* ── static pricing data ─────────────────────────────────── */

const PLAN_DATA = {
  free: {
    name: 'Free',
    tagline: 'Measure and preview, no commitment',
    monthlyPrice: 0,
    annualPrice: 0,
    annualMonthly: 0,
    annualSavings: 0,
    features: [
      '3 reports per month (view only)',
      'Satellite roof measurements',
      'Pitch-adjusted area & squares',
      'On-screen material estimate',
      'Email support',
    ],
  },
  pro: {
    name: 'Pro',
    tagline: 'Unlimited reports for the solo shingle roofer',
    monthlyPrice: 19,
    annualPrice: 190,
    annualMonthly: 15.83,
    annualSavings: 38,
    features: [
      'Unlimited measurements & reports',
      'PDF report downloads',
      'Gutter estimates',
      'Multi-section roofs',
      'Priority email support',
    ],
  },
  contractor: {
    name: 'Contractor',
    tagline: 'The full toolkit — any roof, any crew',
    monthlyPrice: 79,
    annualPrice: 790,
    annualMonthly: 65.83,
    annualSavings: 158,
    features: [
      'Everything in Pro',
      'Tile & metal material types',
      'Valley & ridge measurements',
      'Editable reports & custom materials',
      'Job Cost Estimator',
      'Shareable client reports + CSV export',
      'Custom branding & white-label',
      'Team members',
    ],
  },
};

const REPORT_PACKS = [
  { id: 'single_report', name: 'Single Report', credits: 1, price: 9.99 },
  { id: 'report_pack_10', name: '10-Pack', credits: 10, price: 79 },
  { id: 'report_pack_25', name: '25-Pack', credits: 25, price: 149 },
  { id: 'report_pack_50', name: '50-Pack', credits: 50, price: 249 },
];

const COMPARISON_FEATURES = [
  { label: 'Monthly reports', free: '3 (view)', pro: 'Unlimited', contractor: 'Unlimited' },
  { label: 'Satellite roof measurements', free: true, pro: true, contractor: true },
  { label: 'Pitch-adjusted area & squares', free: true, pro: true, contractor: true },
  { label: 'On-screen material estimate', free: true, pro: true, contractor: true },
  { label: 'PDF report download', free: false, pro: true, contractor: true },
  { label: 'Gutter estimates', free: false, pro: true, contractor: true },
  { label: 'Multi-section roofs', free: false, pro: true, contractor: true },
  { label: 'Tile & metal material types', free: false, pro: false, contractor: true },
  { label: 'Valley & ridge measurements', free: false, pro: false, contractor: true },
  { label: 'Editable reports & quantities', free: false, pro: false, contractor: true },
  { label: 'Custom materials', free: false, pro: false, contractor: true },
  { label: 'Job Cost Estimator', free: false, pro: false, contractor: true },
  { label: 'CSV export', free: false, pro: false, contractor: true },
  { label: 'Shareable client reports', free: false, pro: false, contractor: true },
  { label: 'Custom branding & white-label', free: false, pro: false, contractor: true },
  { label: 'Team members', free: false, pro: false, contractor: true },
  { label: 'Support', free: 'Email', pro: 'Priority', contractor: 'Priority' },
];

/* ── component ───────────────────────────────────────────── */

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();

  const [tiers, setTiers] = useState([]);
  const [reportPacks, setReportPacks] = useState([]);
  const [currentTier, setCurrentTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);

  // ROI calculator — compares against the Contractor plan (everything included)
  const [calcReports, setCalcReports] = useState(15);
  const calcEagleViewCost = calcReports * 50;
  const calcSCCost = isAnnual ? 65.83 : 79;
  const calcMonthlySavings = Math.max(Math.round(calcEagleViewCost - calcSCCost), 0);
  const calcAnnualSavings = calcMonthlySavings * 12;

  useEffect(() => {
    loadPricingData();
  }, []);

  // One-click upsell deep link (?plan=contractor|pro&billing=annual): email CTAs land
  // here and go straight into an authed Stripe checkout; logged-out visitors get the
  // signup modal first and can click through after.
  const autoCheckoutFired = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (!plan || !['pro', 'contractor'].includes(plan) || autoCheckoutFired.current) return;
    if (params.get('billing') === 'annual') setIsAnnual(true);
    if (isAuthenticated) {
      autoCheckoutFired.current = true;
      handleUpgrade(params.get('billing') === 'annual' ? `subscription_${plan}_annual` : `subscription_${plan}`);
    } else {
      autoCheckoutFired.current = true;
      openAuthModal('register');
    }
  }, [isAuthenticated]); // eslint-disable-line

  const loadPricingData = async () => {
    try {
      const [tiersRes, usageRes] = await Promise.all([
        getSubscriptionTiers(),
        isAuthenticated ? getUsage() : Promise.resolve({ data: { subscription_tier: 'free' } })
      ]);
      setTiers(tiersRes.data.tiers);
      setReportPacks(tiersRes.data.report_packs);
      setCurrentTier(usageRes.data.subscription_tier);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleUpgrade = async (productType) => {
    if (!isAuthenticated) {
      openAuthModal('register');
      return;
    }

    setCheckoutLoading(productType);
    try {
      // Track begin checkout event
      const priceMap = {
        'subscription_pro': { id: 'pro', name: 'Pro Subscription', price: 19 },
        'subscription_pro_annual': { id: 'pro_annual', name: 'Pro Annual Subscription', price: 190 },
        'subscription_contractor': { id: 'contractor', name: 'Contractor Subscription', price: 79 },
        'subscription_contractor_annual': { id: 'contractor_annual', name: 'Contractor Annual Subscription', price: 790 },
        'single_report': { id: 'single_report', name: 'Single Report', price: 9.99 },
        'report_pack_10': { id: 'report_pack_10', name: '10 Report Pack', price: 79 },
        'report_pack_25': { id: 'report_pack_25', name: '25 Report Pack', price: 149 },
        'report_pack_50': { id: 'report_pack_50', name: '50 Report Pack', price: 249 }
      };
      const item = priceMap[productType] || { id: productType, name: productType, price: 0 };
      trackBeginCheckout(item, item.price);
      trackUpgradeClicked(currentTier, productType);

      const originUrl = window.location.origin;
      const response = await createCheckout(productType, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error creating checkout session. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  /** Returns the correct product type string for a given tier id */
  const getProductType = (tierId) => {
    if (tierId === 'free') return null;
    return isAnnual ? `subscription_${tierId}_annual` : `subscription_${tierId}`;
  };

  /* ── render helpers ──────────────────────────────────────── */

  const renderComparisonCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-400 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-slate-600 mx-auto" />;
    return <span className="text-xs sm:text-sm font-medium text-slate-300 leading-tight">{value}</span>;
  };

  /* ── JSX ─────────────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-slate-950" data-testid="pricing-page">
      <Helmet>
        <title>Pricing — Unlimited Roof Reports &amp; Full Takeoff Toolkit | SquareCount</title>
        <meta name="description" content="Unlimited satellite roof measurements, editable takeoffs, job costing, and branded reports — up to 90% cheaper than EagleView. Compare Free, Pro, and Contractor plans. Free 7-day Contractor trial, no contracts." />
        <meta property="og:title" content="SquareCount Pricing — Unlimited Roof Reports &amp; Full Takeoff Toolkit" />
        <meta property="og:description" content="Unlimited satellite roof measurements, editable takeoffs, and job costing — up to 90% cheaper than EagleView. Free 7-day Contractor trial." />
        <link rel="canonical" href="https://squarecount.io/pricing" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "SquareCount Roof Measurement Software",
          "description": "Satellite-based roof measurement software for roofing contractors. Instant measurements, PDF reports, and material lists.",
          "brand": { "@type": "Brand", "name": "SquareCount" },
          "image": "https://squarecount.io/logo.png",
          "url": "https://squarecount.io/pricing",
          "offers": [
            {
              "@type": "Offer",
              "name": "Free Plan",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "description": "3 roof measurements per month, view-only with on-screen estimates"
            },
            {
              "@type": "Offer",
              "name": "SquareCount Pro",
              "price": "19.00",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2027-12-31",
              "description": "Unlimited satellite roof measurements with PDF reports, gutter estimates, and multi-section roofs"
            },
            {
              "@type": "Offer",
              "name": "SquareCount Contractor",
              "price": "79.00",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2027-12-31",
              "description": "Shingle, tile, and metal material lists, valley and ridge measurements, editable reports, job cost estimator, shareable client reports, custom branding, and team members"
            }
          ]
        })}</script>
      </Helmet>
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* ────────── HEADER ────────── */}
        <div className="text-center mb-10">
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">
            Roof Measurement Software Pricing
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Save up to 90% compared to EagleView. Get accurate roof measurements instantly.
          </p>
          {!isAuthenticated && (
            <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 rounded-full px-4 py-2 mt-5">
              <Check className="w-4 h-4 text-orange-400" />
              <span className="text-orange-200 text-sm font-medium">
                Every plan starts with a free 7-day Contractor trial &mdash; no credit card required.
              </span>
            </div>
          )}
        </div>

        {/* ────────── ANNUAL / MONTHLY TOGGLE ────────── */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <span
            className={`text-sm font-semibold cursor-pointer transition-colors ${!isAnnual ? 'text-white' : 'text-slate-500'}`}
            onClick={() => setIsAnnual(false)}
          >
            Monthly
          </span>

          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
              isAnnual ? 'bg-orange-500' : 'bg-slate-700'
            }`}
            aria-label="Toggle annual billing"
            data-testid="billing-toggle"
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                isAnnual ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>

          <span
            className={`text-sm font-semibold cursor-pointer transition-colors ${isAnnual ? 'text-white' : 'text-slate-500'}`}
            onClick={() => setIsAnnual(true)}
          >
            Annual
          </span>
          {isAnnual && (
            <span className="ml-1 text-xs font-bold text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
              Save 17%
            </span>
          )}
        </div>

        {/* ────────── TIER CARDS ────────── */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {['free', 'pro', 'contractor'].map((tierId) => {
            const plan = PLAN_DATA[tierId];
            const isCurrentPlan = isAuthenticated && tierId === currentTier;
            const isPopular = tierId === 'contractor';
            const productType = getProductType(tierId);

            const displayPrice = isAnnual ? plan.annualPrice : plan.monthlyPrice;
            const period = isAnnual ? 'yr' : 'mo';

            return (
              <div
                key={tierId}
                className={`relative rounded-2xl p-6 sm:p-8 flex flex-col transition-all ${
                  isPopular
                    ? 'order-first md:order-none bg-white/[0.04] backdrop-blur-xl border border-orange-500/30 z-10 md:scale-[1.02]'
                    : 'bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-slate-700'
                }`}
                style={isPopular ? {
                  boxShadow: '0 0 60px -12px rgba(249,115,22,0.15)',
                } : undefined}
                data-testid={`tier-card-${tierId}`}
              >
                {/* Popular badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-xs font-bold uppercase px-4 py-1.5 rounded-full tracking-wide shadow-lg shadow-orange-500/30">
                      Most Popular
                    </span>
                  </div>
                )}
                {isPopular && (
                  <p className="text-center text-xs text-orange-300/80 font-medium -mt-1 mb-2">
                    Best value for roofing companies
                  </p>
                )}

                {/* Current Plan badge */}
                {isCurrentPlan && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-slate-700 text-slate-300 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Tier name & tagline */}
                <div className="text-center mb-6">
                  <h3 className="font-heading font-bold text-2xl text-white mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-slate-400 text-sm">{plan.tagline}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-heading font-bold text-white">
                      ${displayPrice}
                    </span>
                    {displayPrice > 0 && (
                      <span className="text-slate-500 text-base font-medium">/{period}</span>
                    )}
                  </div>
                  {isAnnual && plan.annualMonthly > 0 && (
                    <p className="text-sm text-green-400 font-medium mt-1">
                      ${plan.annualMonthly}/mo &middot; Save ${plan.annualSavings}/yr
                    </p>
                  )}
                  {!isAnnual && displayPrice === 0 && (
                    <p className="text-sm text-slate-500 mt-1">Free forever</p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <Check className={`w-5 h-5 shrink-0 mt-0.5 ${isPopular ? 'text-orange-400' : 'text-green-400'}`} />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {tierId === 'pro' && (
                  <p className="text-xs text-slate-500 -mt-4 mb-6">
                    Shingle-only. No report editing, job costing, or branding — those are Contractor features.
                  </p>
                )}

                {/* CTA */}
                {isCurrentPlan ? (
                  <Button
                    disabled
                    className="w-full h-12 font-heading font-bold uppercase tracking-wide bg-slate-800 text-slate-500 cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : tierId === 'free' ? (
                  <Button
                    variant="outline"
                    onClick={() => isAuthenticated ? navigate('/dashboard') : openAuthModal('register')}
                    className="w-full h-12 font-heading font-bold uppercase tracking-wide border-2 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white btn-secondary"
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => handleUpgrade(productType)}
                      disabled={checkoutLoading === productType}
                      data-testid={`upgrade-${tierId}-btn`}
                      className={`w-full h-12 font-heading font-bold uppercase tracking-wide ${
                        isPopular
                          ? 'bg-orange-500 hover:bg-orange-400 text-white btn-primary'
                          : 'border-2 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white btn-secondary'
                      }`}
                    >
                      {checkoutLoading === productType ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          {isAuthenticated
                            ? `Start ${plan.name} Plan`
                            : isPopular
                              ? 'Start Your 7-Day Contractor Trial'
                              : `Choose ${plan.name}`}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                    {isPopular ? (
                      <p className="text-center text-xs text-slate-500 mt-2">
                        {!isAuthenticated && <>7-day free trial &middot; No card &middot; </>}30-day money-back guarantee
                      </p>
                    ) : !isAuthenticated && (
                      <p className="text-center text-xs text-slate-500 mt-2">
                        7-day Contractor trial included &middot; No card
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* ────────── REPORT PACKS ────────── */}
        <section className="mb-20">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl text-white mb-2">
              Need Reports Without a Subscription?
            </h2>
            <p className="text-slate-400 text-lg">
              Buy report credits a la carte. No monthly commitment required.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {REPORT_PACKS.map((pack) => (
              <div
                key={pack.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm p-6 text-center hover:border-slate-700 transition-all"
                data-testid={`pack-${pack.id}`}
              >
                <p className="font-heading font-bold text-lg uppercase mb-2 text-white">
                  {pack.name}
                </p>
                <p className="text-4xl font-heading font-bold text-white mb-1">
                  ${pack.price}
                </p>
                {pack.credits > 1 && (
                  <p className="text-sm text-green-400 font-medium mb-1">
                    ${(pack.price / pack.credits).toFixed(2)}/report
                  </p>
                )}
                {pack.credits > 1 && (
                  <span className="inline-block bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded mb-4">
                    Save {Math.round((1 - pack.price / pack.credits / 9.99) * 100)}%
                  </span>
                )}
                {pack.credits === 1 && <div className="mb-5" />}
                <Button
                  onClick={() => handleUpgrade(pack.id)}
                  disabled={checkoutLoading === pack.id}
                  variant="outline"
                  className="w-full font-heading font-bold uppercase tracking-wide border-2 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white btn-secondary"
                >
                  {checkoutLoading === pack.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Buy Now'
                  )}
                </Button>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-6 text-sm">
            Buying the 10-Pack at $79? The <span className="text-orange-400 font-medium">Contractor plan is the same $79/mo</span> with
            unlimited reports plus editing, job costing, and branding.
          </p>
        </section>

        {/* ────────── COMPARISON TABLE (always visible) ────────── */}
        <section className="mb-20">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-6" data-testid="comparison-heading">
            Detailed Feature Comparison
          </h2>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm overflow-x-auto" data-testid="comparison-table">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-3 sm:px-6 py-3 sm:py-4 font-heading font-bold text-slate-300 uppercase">Feature</th>
                  <th className="text-center px-1.5 sm:px-3 py-3 sm:py-4 font-heading font-bold text-slate-300 uppercase w-16 sm:w-28">Free</th>
                  <th className="text-center px-1.5 sm:px-3 py-3 sm:py-4 font-heading font-bold text-slate-300 uppercase w-16 sm:w-28">Pro</th>
                  <th className="text-center px-1.5 sm:px-3 py-3 sm:py-4 font-heading font-bold text-orange-400 uppercase w-[4.5rem] sm:w-28 bg-orange-500/[0.05]">Contractor</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_FEATURES.map((row, idx) => (
                  <tr key={idx} className={`border-b border-slate-800/50 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-slate-800/20'}`}>
                    <td className="px-3 sm:px-6 py-3 text-slate-300 font-medium">{row.label}</td>
                    <td className="px-1.5 sm:px-3 py-3 text-center">{renderComparisonCell(row.free)}</td>
                    <td className="px-1.5 sm:px-3 py-3 text-center">{renderComparisonCell(row.pro)}</td>
                    <td className="px-1.5 sm:px-3 py-3 text-center bg-orange-500/[0.05]">{renderComparisonCell(row.contractor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ────────── ROI CALCULATOR ────────── */}
        <section className="mb-20 rounded-2xl p-8 md:p-12 bg-slate-900/50 backdrop-blur-sm border border-slate-800" data-testid="pricing-savings-calculator">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-8">
            <div className="flex items-center gap-2 bg-green-500/20 text-green-400 px-3 py-1.5 rounded-full">
              <Calculator className="w-4 h-4" />
              <span className="font-medium text-sm">Switch &amp; Save</span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white">
              How Much Are You Spending on EagleView?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Slider */}
            <div>
              <label className="block text-sm uppercase text-slate-400 mb-4">
                Reports per month
              </label>
              <Slider
                value={[calcReports]}
                onValueChange={(v) => setCalcReports(v[0])}
                min={1}
                max={100}
                step={1}
                className="w-full mb-4"
                data-testid="pricing-calc-slider"
              />
              <div className="flex justify-between text-sm text-slate-500">
                <span>1</span>
                <span className="font-bold text-orange-500 text-lg">{calcReports} reports</span>
                <span>100</span>
              </div>
            </div>

            {/* Cost comparison cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/20 text-center">
                <p className="text-[11px] uppercase text-red-400 mb-1">EagleView Cost</p>
                <p className="font-heading font-bold text-3xl text-red-400">${calcEagleViewCost.toLocaleString()}</p>
                <p className="text-[11px] text-red-400/60 mt-0.5">/month</p>
              </div>
              <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/20 text-center">
                <p className="text-[11px] uppercase text-green-400 mb-1">SquareCount Contractor</p>
                <p className="font-heading font-bold text-3xl text-green-400">${calcSCCost}</p>
                <p className="text-[11px] text-green-400/60 mt-0.5">/month {isAnnual ? '(annual)' : ''} &middot; everything included</p>
              </div>
              <div className="col-span-2 bg-orange-500 rounded-xl p-5 text-center">
                <p className="text-[11px] uppercase text-orange-100 mb-1">Your Annual Savings</p>
                <p className="font-heading font-black text-4xl text-white">${calcAnnualSavings.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Button
              onClick={() => handleUpgrade(isAnnual ? 'subscription_contractor_annual' : 'subscription_contractor')}
              disabled={checkoutLoading === 'subscription_contractor' || checkoutLoading === 'subscription_contractor_annual'}
              data-testid="pricing-calc-cta"
              className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold uppercase tracking-wide text-base btn-primary"
            >
              {checkoutLoading === 'subscription_contractor' || checkoutLoading === 'subscription_contractor_annual' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Switch to Contractor &mdash; Save ${calcMonthlySavings.toLocaleString()}/mo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </section>

        {/* ────────── TRUST & GUARANTEE ────────── */}
        <section className="mb-20">
          <div className="flex flex-col md:flex-row items-center justify-center gap-10 text-center md:text-left">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                <Shield className="w-7 h-7 text-orange-400" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-white">30-Day Money-Back Guarantee</p>
                <p className="text-slate-400 text-sm">Not satisfied? Get a full refund, no questions asked.</p>
              </div>
            </div>

            <div className="hidden md:block w-px h-12 bg-slate-800" />

            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                <Zap className="w-7 h-7 text-orange-400" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-white">Cancel Anytime. No Contracts.</p>
                <p className="text-slate-400 text-sm">Downgrade or cancel your subscription whenever you want.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ────────── BOTTOM CTA ────────── */}
        <section
          className="text-center rounded-2xl p-10 md:p-14 border border-slate-800 relative overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(249,115,22,0.08) 0%, rgba(15,23,42,0.5) 70%)',
          }}
        >
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-4">
              <DollarSign className="w-4 h-4" />
              <span className="font-medium text-sm">Compare to EagleView at $40-75/report</span>
            </div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
              Estimate-Grade. Fraction of the Price.
            </h3>
            <p className="text-slate-400 max-w-2xl mx-auto mb-6">
              We use high-resolution satellite imagery and advanced algorithms to deliver
              the same professional-grade measurements you expect, without the premium price tag.
            </p>
            <Button
              onClick={() => isAuthenticated ? navigate('/dashboard') : openAuthModal('register')}
              className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold uppercase tracking-wide btn-primary"
            >
              Get Started Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
